/**
 * @typedef {Object} NavbarLink
 * @property {string|number} [id] - Payload document id when present (live preview)
 * @property {string} title
 * @property {string} [mobileTitle]
 * @property {string} [route]
 * @property {NavbarLink[]} [items]
 * @property {boolean} [categoryTitle]
 * @property {string} [icon]
 * @property {string} [badge]
 * @property {string} [img]
 * @property {string} [imgAlt]
 * @property {boolean} [isExternal]
 */

/**
 * Payload's navbar API already resolves media docs to a CDN-relative path
 * (e.g. `nav/poker-menu.jpg`, no domain) — same convention as ResponsiveImage
 * elsewhere in the app. Just trim/normalize, no URL parsing needed here.
 */
export const toNavbarImagePath = (img) => {
  if (!img || typeof img !== "string") return undefined;
  const trimmed = img.trim();
  if (!trimmed) return undefined;

  return trimmed.replace(/^\/+/, "");
};

// Plain string fields copied over as-is (only when non-empty) — see assignStringFields.
const NAVBAR_STRING_FIELDS = ["mobileTitle", "route", "icon", "badge", "imgAlt"];

/** Copies each non-empty string field from `node` to `mapped`, skipping blanks/wrong types. */
function assignStringFields(mapped, node, keys) {
  keys.forEach((key) => {
    if (typeof node[key] === "string" && node[key]) {
      mapped[key] = node[key];
    }
  });
}

/**
 * Maps a raw Payload navbar-item node to view-ready shape.
 * Values are already localized/resolved server-side — see `isResolved` in
 * `renderLinks` (navigation.js), which skips i18n key resolution for the
 * whole tree when it came from Payload, rather than flagging each node.
 * @param {Record<string, unknown>} node
 * @returns {NavbarLink}
 */
export function mapNavbarNode(node) {
  if (!node || typeof node !== "object") return { title: "" };

  const mapped = {
    title: typeof node.title === "string" ? node.title : "",
  };

  if (node.id != null) mapped.id = node.id;

  assignStringFields(mapped, node, NAVBAR_STRING_FIELDS);

  if (node.categoryTitle) mapped.categoryTitle = true;
  if (node.isExternal) mapped.isExternal = true;

  const img = toNavbarImagePath(node.img);
  if (img) mapped.img = img;

  if (Array.isArray(node.items) && node.items.length > 0) {
    mapped.items = node.items.map(mapNavbarNode);
  }

  return mapped;
}

/**
 * @param {unknown} response - `GET /api/navbar` body (array of nodes)
 * @returns {NavbarLink[]}
 */
export function mapNavbarResponse(response) {
  if (!Array.isArray(response)) return [];
  return response.map(mapNavbarNode);
}
